import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Identity security posture (ISPM)',
    metaDescription:
      'ISPM, risk scoring, and ITDR on one identity graph. Monosign correlates human, machine, and AI-agent identities, scores posture explainably, remediates findings in one click, and rolls it all up into an A-F Identity Security Grade.',
    heroEyebrow: 'Use cases · Identity security posture',
    heroTitle: ['Know your identity posture.', 'Then improve it, weekly.'],
    heroBody:
      'Identity is the modern attack surface, yet posture, risk, and threat detection usually live in three separate tools that only exchange tickets. Monosign runs ISPM, risk scoring, and ITDR on one identity graph and one score. And because it is also your IdP and IGA, findings get fixed, not just filed.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'One identity graph',
      'Explainable risk scores',
      'A-F Identity Security Grade',
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'See it, score it, fix it, defend it.',
    pillarsBody:
      'Posture without remediation is just a report. Because Monosign owns the identities it scores, every finding is one click away from being resolved.',
    pillars: [
      {
        title: 'See everything',
        description:
          'The unified identity graph (IVIP) correlates every identity (human, machine, and AI agent) into one picture of who can reach what, and how.',
        bullets: [
          'Human, machine, and AI-agent identities correlated',
          'Effective access with the exact path that grants it',
          'Blast-radius analysis per identity and entitlement',
          'One graph across directories, apps, and infrastructure',
        ],
      },
      {
        title: 'Score it, explainably',
        description:
          'Configurable risk scores on users, apps, and entitlements: built from posture factors and behavioral detections, rolled up into an A-F Identity Security Grade the board can read. No black box.',
        bullets: [
          'Posture factors: MFA gaps, dormant accounts, password age, orphans',
          'Behavioral detections feed the same score',
          'Configurable weights per factor',
          'Factor-level breakdown behind every score',
        ],
      },
      {
        title: 'Harden it',
        description:
          'Findings ship with the fix attached. Remediate in one click, preview with dry-run, undo if needed, and let certification spend human attention only where risk lives.',
        bullets: [
          'One-click remediation with dry-run and undo',
          'Peer-group outliers surface excessive access',
          'Access dormancy flags entitlements nobody uses',
          'Risk-based auto-certification keeps humans on risky rows',
        ],
      },
      {
        title: 'Detect and respond',
        description:
          'When posture is not enough, ITDR takes over: detections mapped to MITRE ATT&CK, automated response, and signals shared with the rest of your stack.',
        bullets: [
          'ITDR detections mapped to MITRE ATT&CK',
          'Automated response: revoke, disable, step-up',
          'SSF/CAEP signal exchange with your ecosystem',
          'Full export to your SIEM',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'One graph. One score. Fixes that actually land.',
    steps: [
      {
        title: 'Correlate',
        description:
          'Connect directories, apps, and infrastructure; the identity graph correlates every human, machine, and AI-agent identity with its effective access.',
      },
      {
        title: 'Score',
        description:
          'Posture factors and behavioral detections combine into explainable risk scores, and into one A-F grade for leadership.',
      },
      {
        title: 'Remediate',
        description:
          'Fix findings in one click with dry-run and undo, let risk-based certification handle the routine, and let ITDR respond to the rest.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'The capabilities behind this use case.',
    related: [
      {
        label: 'Identity Intelligence (IVIP)',
        description: 'The unified identity graph behind the posture.',
        href: '/sign/identity-intelligence',
      },
      {
        label: 'Security Score',
        description: 'Explainable, configurable risk scoring, down to the factor.',
        href: '/sign/security-score',
      },
      {
        label: 'Blog: what is IVIP?',
        description: 'Identity visibility and intelligence, explained.',
        href: '/blog/what-is-identity-visibility-and-intelligence-ivip',
      },
    ],
  },
  tr: {
    metaTitle: 'Kimlik güvenliği duruşu (ISPM)',
    metaDescription:
      'ISPM, risk skorlama ve ITDR tek kimlik grafiğinde. Monosign insan, makine ve AI-ajan kimliklerini ilişkilendirir, duruşu açıklanabilir şekilde skorlar, bulguları tek tıkla iyileştirir ve hepsini A-F Kimlik Güvenliği Notu’na dönüştürür.',
    heroEyebrow: 'Kullanım senaryoları · Kimlik güvenliği duruşu',
    heroTitle: ['Kimlik duruşunuzu bilin.', 'Sonra her hafta iyileştirin.'],
    heroBody:
      'Kimlik, modern saldırı yüzeyi; ama duruş, risk ve tehdit tespiti genellikle birbirine yalnızca ticket gönderen üç ayrı araçta yaşar. Monosign ISPM’i, risk skorlamayı ve ITDR’ı tek kimlik grafiği ve tek skor üzerinde çalıştırır. Üstelik aynı zamanda IdP ve IGA’nız olduğu için bulgular dosyalanmakla kalmaz, düzeltilir.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'Tek kimlik grafiği',
      'Açıklanabilir risk skorları',
      'A-F Kimlik Güvenliği Notu',
    ],
    pillarsEyebrow: 'Ne elde edersiniz',
    pillarsTitle: 'Görün, skorlayın, düzeltin, savunun.',
    pillarsBody:
      'İyileştirmesi olmayan duruş bir rapordan ibarettir. Monosign skorladığı kimliklerin aynı zamanda sahibi olduğu için her bulgu çözüme bir tık uzaklıktadır.',
    pillars: [
      {
        title: 'Her şeyi görün',
        description:
          'Birleşik kimlik grafiği (IVIP) her kimliği (insan, makine ve AI ajanı) kimin neye, hangi yoldan erişebildiğini gösteren tek resimde ilişkilendirir.',
        bullets: [
          'İnsan, makine ve AI-ajan kimlikleri ilişkilendirilir',
          'Efektif erişim, onu veren yolun tam haritasıyla',
          'Kimlik ve yetki başına blast radius analizi',
          'Dizinler, uygulamalar ve altyapı genelinde tek grafik',
        ],
      },
      {
        title: 'Açıklanabilir şekilde skorlayın',
        description:
          'Kullanıcı, uygulama ve yetkilerde yapılandırılabilir risk skorları: duruş faktörleri ve davranışsal tespitlerden kurulur, yönetim kurulunun okuyabileceği A-F Kimlik Güvenliği Notu’nda toplanır. Kara kutu yok.',
        bullets: [
          'Duruş faktörleri: MFA eksikleri, atıl hesaplar, parola yaşı, sahipsiz hesaplar',
          'Davranışsal tespitler aynı skoru besler',
          'Faktör başına yapılandırılabilir ağırlıklar',
          'Her skorun arkasında faktör seviyesinde döküm',
        ],
      },
      {
        title: 'Sertleştirin',
        description:
          'Bulgular düzeltmesiyle birlikte gelir. Tek tıkla iyileştirin, dry-run ile önizleyin, gerekirse geri alın; sertifikasyon insan dikkatini yalnızca riskin olduğu yere harcasın.',
        bullets: [
          'Dry-run ve geri alma ile tek tık iyileştirme',
          'Akran grubu aykırılıkları fazla erişimi ortaya çıkarır',
          'Erişim atıllığı kimsenin kullanmadığı yetkileri işaretler',
          'Risk bazlı otomatik sertifikasyon insanı riskli satırlarda tutar',
        ],
      },
      {
        title: 'Tespit edin ve yanıt verin',
        description:
          'Duruş yetmediğinde ITDR devreye girer: MITRE ATT&CK ile eşlenmiş tespitler, otomatik yanıt ve yığınınızın geri kalanıyla paylaşılan sinyaller.',
        bullets: [
          'MITRE ATT&CK ile eşlenmiş ITDR tespitleri',
          'Otomatik yanıt: erişim iptali, devre dışı bırakma, step-up',
          'Ekosisteminizle SSF/CAEP sinyal alışverişi',
          'SIEM’inize tam dışa aktarım',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Tek grafik. Tek skor. Gerçekten yapılan düzeltmeler.',
    steps: [
      {
        title: 'İlişkilendirin',
        description:
          'Dizinleri, uygulamaları ve altyapıyı bağlayın; kimlik grafiği her insan, makine ve AI-ajan kimliğini efektif erişimiyle ilişkilendirsin.',
      },
      {
        title: 'Skorlayın',
        description:
          'Duruş faktörleri ve davranışsal tespitler açıklanabilir risk skorlarında ve yönetim için tek bir A-F notunda birleşir.',
      },
      {
        title: 'İyileştirin',
        description:
          'Bulguları dry-run ve geri almayla tek tıkta düzeltin; rutini risk bazlı sertifikasyona, kalanını ITDR’a bırakın.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'Bu senaryonun arkasındaki yetenekler.',
    related: [
      {
        label: 'Identity Intelligence (IVIP)',
        description: 'Duruşun arkasındaki birleşik kimlik grafiği.',
        href: '/sign/identity-intelligence',
      },
      {
        label: 'Security Score',
        description: 'Faktör seviyesine kadar açıklanabilir, yapılandırılabilir risk skorlama.',
        href: '/sign/security-score',
      },
      {
        label: 'Blog: IVIP nedir?',
        description: 'Kimlik görünürlüğü ve zekâsı, açık anlatımla.',
        href: '/blog/ivip-nedir-kimin-neye-erisimi-var',
      },
    ],
  },
}

type Props = { locale: string }

export default function IdentitySecurityPosturePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/solutions/identity-security-posture"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
